import type { MovieCard as MovieCardModel } from '@zyx/shared';
import { Bookmark, BookmarkCheck, Heart, ListPlus, Play } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '@/hooks/use-connection';
import { useUpdateMark } from '@/hooks/use-movies';
import { editionLabel, formatAgo, formatRuntime, progressFraction } from '@/lib/format';
import { urls } from '@/lib/urls';
import { cn } from '@/lib/utils';
import { AssetImage } from './asset';
import { ListMenu } from './list-menu';

/** Shown only when it says something: a movie in one ordinary edition needs no badge. */
function editionBadges(movie: MovieCardModel): ReadonlyArray<string> {
  if (movie.editions.length === 1 && movie.editions[0] === 'censored') {
    return [];
  }
  return movie.editions.map(editionLabel);
}

const ACTION_CLASSES =
  'rounded p-0.5 text-muted-foreground transition-theme hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:opacity-100';

/**
 * One movie: its artwork in the source's own 3:2 (wide DVD-jacket covers, which a
 * portrait crop would halve), the code and title, and what the viewer can do with it.
 * Hovering or focusing lifts it, plays the hover clip and offers Play. `menu` adds
 * controls of the page showing the card, such as moving it within a list.
 */
export function MovieCard(props: { readonly movie: MovieCardModel; readonly menu?: ReactNode; readonly className?: string }) {
  const { movie } = props;
  const api = useApi();
  const update = useUpdateMark(movie.code);
  // The clip loads only on hover: a shelf of autoplaying videos would be tens of MB.
  const [hovering, setHovering] = useState(false);
  const fraction = progressFraction(movie.progress);
  const inWatchlist = movie.mark.inWatchlist;
  // An older service sends no `listIds`.
  const onLists = movie.mark.listIds?.length ?? 0;
  const resuming = movie.progress !== undefined && !movie.progress.finished;

  return (
    <div className={cn('group/card flex min-w-0 flex-col gap-2.5', props.className)} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <Link
        to={urls.movie(movie.code)}
        className={cn(
          'relative block aspect-[3/2] overflow-hidden rounded-card bg-muted shadow-card transition-theme',
          'motion-safe:group-hover/card:scale-[var(--hover-scale)] group-hover/card:shadow-card-hover group-hover/card:ring-1 group-hover/card:ring-[var(--glow)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:focus-visible:scale-[var(--hover-scale)]',
        )}
        aria-label={`${movie.code}: ${movie.title}`}
      >
        <AssetImage file={movie.cover} alt="" className="size-full" />
        {hovering && movie.previewVideo !== undefined ? <video src={api.assetUrl(movie.previewVideo)} className="absolute inset-0 size-full object-cover" autoPlay muted loop playsInline /> : null}
        <div className="absolute left-2 top-2 flex gap-1">
          {editionBadges(movie).map((label) => (
            <span key={label} className="rounded-button bg-primary/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
              {label}
            </span>
          ))}
        </div>
        {/* Revealed on hover or focus: a shade to read against, and Play. */}
        <div className="absolute inset-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/85 via-black/20 to-transparent p-2.5 opacity-0 transition-theme group-hover/card:opacity-100 group-focus-within/card:opacity-100">
          <span className="cta flex size-9 items-center justify-center rounded-full shadow-card" aria-hidden>
            <Play className="size-4 translate-x-px fill-current" />
          </span>
          <span className="truncate text-right text-[11px] font-medium text-white/85">{resuming ? 'Resume' : (movie.releaseDate?.slice(0, 4) ?? '')}</span>
        </div>
        {fraction === undefined ? null : (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-black/50">
            <div className="h-full bg-primary" style={{ width: `${fraction * 100}%` }} />
          </div>
        )}
      </Link>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Link to={urls.movie(movie.code)} className="block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" tabIndex={-1}>
            <p className="font-mono text-xs font-semibold text-primary">{movie.code}</p>
            <p className="line-clamp-2 break-words text-sm leading-snug text-foreground/85 transition-theme group-hover/card:text-foreground" title={movie.title}>
              {movie.title}
            </p>
          </Link>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{[formatAgo(movie.publishedAt) ?? movie.releaseDate, formatRuntime(movie.durationS)].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
          {movie.mark.favourite ? <Heart className="size-3.5 fill-primary text-primary" aria-label="Favourite" /> : null}
          <button
            type="button"
            aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
            title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
            disabled={update.isPending}
            onClick={() => update.mutate({ inWatchlist: !inWatchlist })}
            className={cn(ACTION_CLASSES, inWatchlist ? 'text-primary opacity-100' : 'opacity-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100')}
          >
            {inWatchlist ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
          </button>
          <ListMenu code={movie.code} listIds={movie.mark.listIds ?? []}>
            <button
              type="button"
              aria-label="Add to a list"
              title={onLists === 0 ? 'Add to a list' : `On ${onLists} list${onLists === 1 ? '' : 's'}`}
              className={cn(ACTION_CLASSES, onLists > 0 ? 'text-primary opacity-100' : 'opacity-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100')}
            >
              <ListPlus className="size-4" />
            </button>
          </ListMenu>
          {props.menu}
        </div>
      </div>
    </div>
  );
}
